import { ReactNode } from 'react';
import { useChat } from '@packages/shared/core/chat/hooks';
import { useLogger, useServices } from '@packages/shared/core/common/hooks';
import { useExperts, useGetRandomExpert } from '@packages/shared/core/experts/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { ChatQuestionStepData, FlowName, QuizScreenProps } from '@packages/shared/core/quiz/entities';

type ChatQuestionStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const ChatQuestionStep = ({ id, renderQuizScreen }: ChatQuestionStepProps) => {
  const logger = useLogger();
  const { back, next, stepData, updateUserPreferences, totalSteps, stepOrder } = useQuizStep(id);

  const { analyticsService } = useServices();
  const { popupEvents, otherEvent } = stepData as ChatQuestionStepData;

  const { expert } = useGetRandomExpert(FlowName.EXPERTS_CATALOGUE);

  const { currentExpertUpdated } = useExperts({
    flowName: FlowName.EXPERTS_CATALOGUE,
  });

  const expertId = expert ? expert.id : '';
  const { sendMessage } = useChat(expertId);

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const { dataKey } = stepData as ChatQuestionStepData;

  const onDraft = async (draft: string) => {
    currentExpertUpdated(expertId);
    sendMessage(draft);
  };

  const onPopupButtonClick = async (buttonLabel: string) => {
    onDraft(buttonLabel);
    if (popupEvents.clickEvent) {
      analyticsService.trackQuizEvent(popupEvents.clickEvent, {
        context: buttonLabel,
      });
    }
  };

  const onPopupOpen = async () => {
    if (popupEvents.openEvent) {
      analyticsService.trackQuizEvent(popupEvents.openEvent, {});
    }
  };

  const goBack = async () => back();

  const onButtonClick = async (text: string) => {
    if (otherEvent) {
      analyticsService.trackQuizEvent(otherEvent, {
        context: text,
      });
    }
  };

  const onChosen = async (text: string, key?: string) => {
    updateUserPreferences(dataKey, text);
    await next(text, key);
  };

  return (
    <>
      {renderQuizScreen({
        onChosen,
        onBack: goBack,
        onButtonClick,
        onPopupButtonClick,
        onPopupOpen,
        totalSteps,
        stepOrder,
        ...(stepData as ChatQuestionStepData),
      })}
    </>
  );
};

export default ChatQuestionStep;
